/* ================================================================
   LFJ SERVICES ROOM — Redesign Checkpoint 7 (Part D §7a)
   ----------------------------------------------------------------
   Three responsibilities:

   1. SNAP DETECTION — IntersectionObserver (threshold 0.6) watches
      all five sections. When a section crosses 60% of the viewport,
      it becomes "active." Observer fires in all modern browsers
      including Safari, which is why it's preferred over the newer
      scrollsnapchange event (Chrome/Firefox only as of 2025).

   2. CLICK SOUND — Short Web Audio API percussive tone on section
      change. No audio file. Square oscillator, descending frequency
      sweep (300→80 Hz), 80ms, gain 0.12. Creates the mechanical
      "thunk" of a section locking into place. AudioContext created
      lazily on first user scroll (scroll is a user gesture — the
      AudioContext will auto-resume). Gated by the svc_click_sound
      data attribute from settings.

   3. NAV — Dot clicks and up/down arrow buttons scroll the
      container to the target section. Keyboard: ArrowDown/PageDown
      → next section, ArrowUp/PageUp → previous. Escape → no-op
      (Services has no modal). All keys only fire when no other
      modal/dialog is open on the page.

   Engine pattern: safeRun + __lfjServicesBound init-guard.
================================================================ */

(function () {
  if (window.__lfjServicesBound) return;
  window.__lfjServicesBound = true;

  function safeRun(label, fn) {
    try { return fn(); }
    catch (e) { console.warn('[LFJ:services] ' + label, e); }
  }

  document.addEventListener('DOMContentLoaded', function () {
    safeRun('init', function () {

      /* ── DOM refs ── */
      var container  = document.getElementById('lfjSvcContainer');
      var dotsEl     = document.getElementById('lfjSvcDots');
      var sections   = container ? Array.prototype.slice.call(
        container.querySelectorAll('.lfj-svc-section')
      ) : [];
      var dots       = dotsEl ? Array.prototype.slice.call(
        dotsEl.querySelectorAll('.lfj-svc-dot')
      ) : [];

      /* ── Settings via data attributes on the wrap element ── */
      var wrapEl = document.getElementById('lfjSvcWrap');
      var clickSoundEnabled = wrapEl
        ? wrapEl.dataset.clickSound !== 'false'
        : true;

      if (!container || !sections.length) return;

      /* ── State ── */
      var currentIdx = 0;
      var audioCtx   = null;

      /* ── Audio ── */
      function getCtx() {
        if (audioCtx) return audioCtx;
        var Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        audioCtx = new Ctx();
        return audioCtx;
      }

      function playClick() {
        if (!clickSoundEnabled) return;
        safeRun('playClick', function () {
          var ctx = getCtx();
          if (!ctx) return;
          if (ctx.state === 'suspended') ctx.resume();

          var osc  = ctx.createOscillator();
          var gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          /* Square wave descending sweep — mechanical click/thunk */
          osc.type = 'square';
          osc.frequency.setValueAtTime(300, ctx.currentTime);
          osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.06);
          gain.gain.setValueAtTime(0.12, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);

          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.1);
        });
      }

      /* ── Active section update ── */
      function setActive(idx) {
        if (idx === currentIdx) return;
        var prev = currentIdx;
        currentIdx = idx;

        /* Update dots */
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === idx);
        });

        /* Play sound (not on the initial activation at load) */
        if (prev !== idx) playClick();
      }

      /* ── IntersectionObserver — snap detection ── */
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            var idx = sections.indexOf(entry.target);
            if (idx !== -1) setActive(idx);
          }
        });
      }, {
        root: container,
        threshold: [0.6]
      });

      sections.forEach(function (section) { observer.observe(section); });

      /* ── Scroll to section ── */
      function scrollTo(idx) {
        safeRun('scrollTo', function () {
          var target = sections[idx];
          if (!target || !container) return;
          container.scrollTo({
            top: target.offsetTop,
            behavior: 'smooth'
          });
        });
      }

      /* ── Dot nav ── */
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          safeRun('dot-click', function () { scrollTo(i); });
        });
      });

      /* ── Arrow button nav ── */
      var upBtns   = container.querySelectorAll('.lfj-svc-arrow[data-dir="up"]');
      var downBtns = container.querySelectorAll('.lfj-svc-arrow[data-dir="down"]');

      upBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          safeRun('up-arrow', function () { scrollTo(currentIdx - 1); });
        });
      });
      downBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          safeRun('down-arrow', function () { scrollTo(currentIdx + 1); });
        });
      });

      /* ── Keyboard nav ── */
      document.addEventListener('keydown', function (e) {
        safeRun('keydown', function () {
          /* Only fire if we're in the services room (container visible)
             and no other modal/dialog is open */
          if (!container) return;
          var rect = container.getBoundingClientRect();
          var inView = rect.top < window.innerHeight && rect.bottom > 0;
          if (!inView) return;
          /* Don't steal keys if a dialog is open */
          if (document.querySelector('[role="dialog"]:not([hidden])')) return;

          if (e.key === 'ArrowDown' || e.key === 'PageDown') {
            e.preventDefault();
            scrollTo(currentIdx + 1);
          } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
            e.preventDefault();
            scrollTo(currentIdx - 1);
          }
        });
      });

      /* ── Initialise first dot ── */
      if (dots[0]) dots[0].classList.add('is-active');

    }); /* end safeRun init */
  }); /* end DOMContentLoaded */

}());
